import {
  GitLabMerge,
  GitLabTag,
  OPSANIO_APT_RINGS,
  OpsanioVersions,
  OpsanioWaves,
} from "src/questionnaires";

/**
 * Derived, presentation-agnostic model for the wave update console.
 *
 * Everything here is a pure function of the six payloads the page loads
 * (versions, tags, merges, appliances, lifecycle, freshness). Keeping the
 * derivations out of the components means the promotion-chain rail, the summary
 * tiles and the detail pane all agree on a single notion of "what state is this
 * wave in" instead of each re-deriving its own.
 *
 * Colours are EUI semantic names only - the app has no `EuiProvider`, dark mode
 * is a stylesheet swap, so a hardcoded hex would not survive a theme change.
 */

/** The pinned component versions shown in the per-wave component table. */
export const VERSION_FIELDS: (keyof OpsanioVersions)[] = [
  "opsanio_plugin_version",
  "opsanio_plugin_tag",
  "wazuh_version",
  "wazuh_rules",
  "opensearch_version",
  "opensearch_dashboard_version",
  "nats_version",
  "nats_processor_version",
  "memgraph_version",
  "mage_version",
  "promtail_version",
  "coredns_version",
  "cert_manager",
  "longhorn",
  "codex",
  "most",
  "cardea",
  "apt_ring",
  "semaphrone",
  "imap_wodle",
  "kubernetes_wodle",
  "netflow_wodle",
  "osp_wodle",
  "promtail_wodle",
  "remotelog_wodle",
  "snmp_wodle",
  "webhook_wodle",
];

/** Floating tags never carry a comparable version number. */
const FLOATING_TAGS = ["test", "latest"];

const isFloating = (value?: string): boolean =>
  !value ||
  FLOATING_TAGS.includes(value) ||
  FLOATING_TAGS.some((t) => value.endsWith(`-${t}`));

/**
 * The APT ring is a per-wave choice, not a version: it has no GitLab tags to be
 * behind, and it is never synced from the parent (see `VERSION_SYNC_FIELDS` on
 * the server) - so it never counts as outdated or as differing from the parent.
 */
const isRingField = (field: keyof OpsanioVersions): boolean =>
  field === "apt_ring";

/**
 * Wave lifecycle as reported by the backend (`GET /waves/lifecycle`), derived
 * there from GitLab. `mr` is the wave's most recent update merge request.
 */
export interface WaveLifecycle {
  state: "locked" | "available" | "in_progress" | "closed" | string;
  isRoot: boolean;
  parentWave?: OpsanioWaves;
  mr?: {
    iid: number;
    state: string;
    web_url: string;
    finalized: boolean;
  } | null;
}

/**
 * Per-appliance detail for one wave (`GET /waves/:wave/appliances`). Mirrors the
 * backend `ApplianceFreshness` shape; shared by the create-update appliance
 * picker and the detail pane's roster.
 */
export interface ApplianceFreshness {
  vpc: string;
  groupPath: string;
  deployed: boolean;
  /** Only meaningful once `forksChecked` - `false` while unchecked. */
  allFresh: boolean;
  /**
   * False while the fork comparison is still pending (the roster is loaded
   * without it, see `useWaveAppliances`).
   */
  forksChecked?: boolean;
  results?: Array<{ leaf: string; upToDate: boolean; behindBy: number | null }>;
  /**
   * Cluster API owns this appliance's cluster: it has no 200-k3-server /
   * 210-k3s forks, so fork freshness is not evaluated and the deploy step runs
   * no Terraform pipelines for it.
   */
  manageViaCapi?: boolean;
  prtg: {
    configured: boolean;
    deviceCount: number;
    missing: boolean;
    link: string | null;
  };
  forkLinks: Array<{ leaf: string; url: string }>;
  pangolinLinks: Array<{ app: string; url: string }>;
  customerContacts: string[];
  contactsDefaulted: boolean;
}

/** Per-wave version freshness (`GET /waves/:wave/version-freshness`). */
export interface WaveFreshness {
  upToDate: boolean;
  parentWave: string;
  behind: Array<{ field: string; parentValue: string; waveValue: string }>;
}

/**
 * The one state vocabulary for a wave. Replaces the three overlapping ones the
 * page used to show side by side (health status, lifecycle badge, blocked hint).
 */
export type WaveViewState =
  | "root"
  | "unconfig"
  | "blocked"
  | "inflight"
  | "updates"
  | "current";

/** EUI semantic colour for text, icons and `EuiHealth`. */
export const waveStateColor = (state: WaveViewState): string =>
  ({
    root: "subdued",
    unconfig: "subdued",
    blocked: "danger",
    inflight: "primary",
    updates: "warning",
    current: "success",
  })[state];

/** `EuiBadge` has no `subdued`; it uses `default` for the neutral case. */
export const waveStateBadgeColor = (state: WaveViewState): string =>
  state === "root" || state === "unconfig" ? "default" : waveStateColor(state);

export const waveStateIcon = (state: WaveViewState): string =>
  ({
    root: "branch",
    unconfig: "minusInCircle",
    blocked: "lock",
    inflight: "merge",
    updates: "bell",
    current: "checkInCircleFilled",
  })[state];

/**
 * Newest patch tag GitLab knows about for a component. `tags` is keyed by
 * component field, then by major version; we take the highest major's
 * `latestPatch`.
 */
export const latestTagFor = (
  tags: any,
  field: keyof OpsanioVersions,
): GitLabTag | undefined => {
  const allMajors = tags?.[field];
  if (!allMajors) return undefined;

  const newestMajor = Object.entries(allMajors)
    .filter(([k]) => !isNaN(Number(k.replace(/^v/, ""))))
    .sort(
      ([a], [b]) => Number(b.replace(/^v/, "")) - Number(a.replace(/^v/, "")),
    )[0];

  return newestMajor ? ((newestMajor[1] as any).latestPatch as GitLabTag) : undefined;
};

/** Numeric dotted-version compare; `-` when either side is not comparable. */
export const compareVersions = (
  current: string,
  latest: string,
): "upToDate" | "outdated" | "-" => {
  if (!current || !latest) return "-";
  const parts = (v: string) => v.replace(/^v/, "").split(".").map(Number);
  const cur = parts(current);
  const lat = parts(latest);

  for (let i = 0; i < Math.max(cur.length, lat.length); i++) {
    const c = cur[i] || 0;
    const l = lat[i] || 0;
    if (c < l) return "outdated";
    if (c > l) return "upToDate";
  }
  return "upToDate";
};

/** One version a component can be pinned to, offered by the manual picker. */
export interface VersionChoice {
  /** The literal value written into the version set, e.g. `v1.4.7`. */
  value: string;
  /**
   * `patch` is a concrete `vX.Y.Z` tag, `minor` the floating `vX.Y` that tracks
   * it, `floating` a channel name such as `test` or `latest`.
   */
  kind: "patch" | "minor" | "floating";
  tag?: GitLabTag;
}

/** Descending semver-ish order; non-numeric parts sort last. */
const versionOrder = (a: string, b: string): number => {
  const parts = (v: string) => v.replace(/^v/, "").split(".").map(Number);
  const l = parts(a);
  const r = parts(b);
  for (let i = 0; i < Math.max(l.length, r.length); i++) {
    const x = isNaN(l[i]) ? -1 : (l[i] ?? -1);
    const y = isNaN(r[i]) ? -1 : (r[i] ?? -1);
    if (x !== y) return y - x;
  }
  return 0;
};

/**
 * Every version the manual picker can offer for one component, newest first.
 *
 * The tag payload is keyed by major and carries only the newest minor and patch
 * per major, so this is the complete set GitLab gives us without a second
 * request: the concrete `vX.Y.Z` plus the floating `vX.Y` that tracks it, and
 * the `test` channel when the project publishes one. The bare `vX` key is
 * synthesised server-side and has no tag behind it - it is never offered.
 *
 * `running` is appended when it is not already in the list, so a component
 * pinned to something GitLab no longer reports still shows its current value.
 */
export const versionChoicesFor = (
  tags: any,
  field: keyof OpsanioVersions,
  running?: string,
): VersionChoice[] => {
  if (isRingField(field))
    return OPSANIO_APT_RINGS.map((ring) => ({ value: ring, kind: "floating" }));

  const majors = tags?.[field];
  const seen = new Set<string>();
  const semver: VersionChoice[] = [];
  const floating: VersionChoice[] = [];

  const push = (into: VersionChoice[], choice: VersionChoice): void => {
    if (!choice.value || seen.has(choice.value)) return;
    seen.add(choice.value);
    into.push(choice);
  };

  Object.entries(majors ?? {}).forEach(([key, entry]: [string, any]) => {
    if (isNaN(Number(key.replace(/^v/, "")))) {
      push(floating, {
        value: entry?.major?.name ?? key,
        kind: "floating",
        tag: entry?.major,
      });
      return;
    }
    if (entry?.latestPatch)
      push(semver, {
        value: entry.latestPatch.name,
        kind: "patch",
        tag: entry.latestPatch,
      });
    if (entry?.latestMinor)
      push(semver, {
        value: entry.latestMinor.name,
        kind: "minor",
        tag: entry.latestMinor,
      });
  });

  FLOATING_TAGS.forEach((name) =>
    push(floating, { value: name, kind: "floating" }),
  );

  semver.sort((a, b) => versionOrder(a.value, b.value));

  const choices = [...semver, ...floating];
  if (running && !seen.has(running))
    choices.push({
      value: running,
      kind: isFloating(running) ? "floating" : "patch",
    });
  return choices;
};

export type ComponentRowState = "outdated" | "current" | "unknown";

export interface ComponentRow {
  field: keyof OpsanioVersions;
  /** Translated, human-readable component name - the sortable table column. */
  name: string;
  running: string;
  parent: string;
  latest: string;
  latestTag?: GitLabTag;
  state: ComponentRowState;
  /** The parent wave pins a different concrete version than this wave. */
  differsFromParent: boolean;
}

/**
 * One row per pinned component, comparing what the wave runs against its parent
 * wave and against the newest GitLab tag.
 */
export const buildComponentRows = (
  version: OpsanioVersions | null,
  parentVersions: OpsanioVersions | null,
  tags: any,
  label: (field: keyof OpsanioVersions) => string,
): ComponentRow[] => {
  if (!version) return [];

  return VERSION_FIELDS.map((field) => {
    const running = (version[field] as string) ?? "";
    const parent = (parentVersions?.[field] as string) ?? "";
    const latestTag = latestTagFor(tags, field);
    const latest = latestTag?.name ?? "";

    const comparable = !isFloating(running) && Boolean(latest);
    const state: ComponentRowState = !comparable
      ? "unknown"
      : compareVersions(running, latest) === "outdated"
        ? "outdated"
        : "current";

    return {
      field,
      name: label(field),
      running,
      parent,
      latest,
      latestTag,
      state,
      differsFromParent:
        !isRingField(field) &&
        Boolean(parent) &&
        !isFloating(parent) &&
        running !== parent,
    };
  });
};

export const componentStateColor = (state: ComponentRowState): string =>
  ({ outdated: "warning", current: "success", unknown: "subdued" })[state];

/** Everything the rail, the tiles and the detail pane need about one wave. */
export interface WaveSummary {
  wave: OpsanioWaves;
  stage: number;
  parentWave: OpsanioWaves | null;
  version: OpsanioVersions | null;
  merge: GitLabMerge | null;
  lifecycle?: WaveLifecycle;
  state: WaveViewState;
  /** Components behind their newest GitLab tag. */
  outdatedCount: number;
  /** Components pinned differently from the parent wave. */
  behindParentCount: number;
  appliances: string[];
  hasOpenMr: boolean;
  isMerged: boolean;
  isFinalized: boolean;
  /** The parent has absorbed its own parent and has no update in flight. */
  parentReady: boolean;
  /** Fields the parent wave itself is still behind on, for the blocked callout. */
  parentBehind: string[];
  parentBlockedByOpenUpdate: boolean;
}

export interface WaveInputs {
  waves: OpsanioWaves[];
  versions: Record<OpsanioWaves, OpsanioVersions | null>;
  merges: Record<OpsanioWaves, GitLabMerge | null> | null;
  tags: any;
  appliances: Record<OpsanioWaves, string[] | null> | null;
  lifecycle: Record<OpsanioWaves, WaveLifecycle> | null;
  freshness: Record<OpsanioWaves, WaveFreshness> | null;
}

/**
 * Build the summary for every wave in promotion order.
 *
 * Promotion is ordered: a wave may only be updated once its PARENT is up to
 * date and has no update in flight, mirroring the server-side
 * "409 Parent wave not up to date" gate. A missing freshness entry fails open on
 * the version dimension - the server still enforces it.
 */
export const buildWaveSummaries = ({
  waves,
  versions,
  merges,
  tags,
  appliances,
  lifecycle,
  freshness,
}: WaveInputs): WaveSummary[] =>
  waves.map((wave, index) => {
    const parentWave = index > 0 ? waves[index - 1] : null;
    const version = versions?.[wave] ?? null;
    const parentVersions = parentWave ? (versions?.[parentWave] ?? null) : null;
    const merge = merges?.[wave] ?? null;
    const parentMerge = parentWave ? (merges?.[parentWave] ?? null) : null;

    const rows = buildComponentRows(version, parentVersions, tags, (f) =>
      String(f),
    );
    const outdatedCount = rows.filter((r) => r.state === "outdated").length;
    const behindParentCount = rows.filter((r) => r.differsFromParent).length;

    const hasOpenMr = merge?.state === "opened";
    const isMerged = merge?.state === "merged";
    const isFinalized =
      lifecycle?.[wave]?.state === "closed" ||
      Boolean(lifecycle?.[wave]?.mr?.finalized);

    const parentFreshness =
      parentWave && parentWave !== "dev" ? freshness?.[parentWave] : undefined;
    const parentBlockedByOpenUpdate =
      parentWave !== null &&
      parentWave !== "dev" &&
      parentMerge?.state === "opened";
    const parentReady =
      parentFreshness?.upToDate !== false && !parentBlockedByOpenUpdate;

    const isRoot = wave === "dev";
    const state: WaveViewState = isRoot
      ? "root"
      : !version
        ? "unconfig"
        : hasOpenMr
          ? "inflight"
          : !parentReady
            ? "blocked"
            : outdatedCount > 0 || behindParentCount > 0
              ? "updates"
              : "current";

    return {
      wave,
      stage: index + 1,
      parentWave,
      version,
      merge,
      lifecycle: lifecycle?.[wave],
      state,
      outdatedCount,
      behindParentCount,
      appliances: appliances?.[wave] ?? [],
      hasOpenMr,
      isMerged,
      isFinalized,
      parentReady,
      parentBehind: (parentFreshness?.behind ?? []).map(
        (b) => `${b.field} (${b.waveValue || "unset"} → ${b.parentValue})`,
      ),
      parentBlockedByOpenUpdate,
    };
  });

export type WaveStepKey =
  | "create"
  | "prepare-monitoring"
  | "deploy"
  | "update-merge-state"
  | "merge"
  | "finalize";

export interface WaveStep {
  key: WaveStepKey;
  /** `EuiStepNumber` status; `current` is rendered as the plain filled number. */
  status: "complete" | "current" | "incomplete" | "disabled";
  /**
   * The step runs a real operation but leaves no state we can read back, so we
   * can only say it is reachable - never that it has happened.
   */
  untracked: boolean;
}

export const WAVE_STEPS: WaveStepKey[] = [
  "create",
  "prepare-monitoring",
  "deploy",
  "update-merge-state",
  "merge",
  "finalize",
];

/**
 * The deployment workflow as far as GitLab actually tells us.
 *
 * Only three of the six steps leave a readable trace: an MR exists (create), the
 * MR is merged (merge) and the MR carries the finalized label (finalize).
 * `prepare-monitoring`, `deploy` and `update-merge-state` are fire-and-forget
 * POSTs - once an update is open they become reachable, and we mark them
 * `untracked` rather than guessing whether the operator has already run them.
 */
export const buildWaveSteps = (summary: WaveSummary): WaveStep[] => {
  const { state, hasOpenMr, isMerged, isFinalized } = summary;
  const started = hasOpenMr || isMerged || isFinalized;
  const blocked = state === "blocked" || state === "unconfig";

  return WAVE_STEPS.map((key) => {
    if (key === "create") {
      return {
        key,
        status: started ? "complete" : blocked ? "disabled" : "current",
        untracked: false,
      };
    }
    if (key === "merge") {
      return {
        key,
        status: isMerged || isFinalized ? "complete" : hasOpenMr ? "current" : "disabled",
        untracked: false,
      };
    }
    if (key === "finalize") {
      return {
        key,
        status: isFinalized ? "complete" : isMerged ? "current" : "disabled",
        untracked: false,
      };
    }
    // prepare-monitoring / deploy / update-merge-state
    return {
      key,
      status: isMerged || isFinalized ? "complete" : hasOpenMr ? "incomplete" : "disabled",
      untracked: hasOpenMr,
    };
  });
};

/** The single action the operator should take next on this wave. */
export type WavePrimaryAction =
  | "create-version"
  | "create-update"
  | "merge"
  | "finalize"
  | "none";

export const primaryActionFor = (summary: WaveSummary): WavePrimaryAction => {
  if (summary.state === "root") return "none";
  if (summary.state === "unconfig") return "create-version";
  if (summary.hasOpenMr) return "merge";
  if (summary.isMerged && !summary.isFinalized) return "finalize";
  if (summary.state === "updates" || summary.state === "blocked")
    return "create-update";
  return "none";
};

/**
 * Pick the wave the operator most likely came here for: whatever is furthest
 * along the "needs me now" scale, falling back to the first configured wave.
 */
export const pickInitialWave = (summaries: WaveSummary[]): OpsanioWaves => {
  const byPriority: WaveViewState[] = ["inflight", "blocked", "updates"];
  for (const state of byPriority) {
    const hit = summaries.find((s) => s.state === state);
    if (hit) return hit.wave;
  }
  return summaries.find((s) => s.state !== "root")?.wave ?? summaries[0].wave;
};

/** Relative time, translated. Mirrors the wording the page used before. */
export const formatTimeAgo = (
  dateString: string | undefined,
  translate: (key: string, opts?: any) => string,
): string => {
  if (!dateString)
    return translate("anketa.opsanio.waves.time.na", { defaultMessage: "N/A" });

  const diff = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000,
  );

  if (diff < 60)
    return translate("anketa.opsanio.waves.time.seconds_ago", {
      values: { count: diff },
      defaultMessage: "{{count}}s ago",
    });
  if (diff < 3600)
    return translate("anketa.opsanio.waves.time.minutes_ago", {
      values: { count: Math.floor(diff / 60) },
      defaultMessage: "{{count}}min ago",
    });
  if (diff < 86400)
    return translate("anketa.opsanio.waves.time.hours_ago", {
      values: { count: Math.floor(diff / 3600) },
      defaultMessage: "{{count}}h ago",
    });
  return translate("anketa.opsanio.waves.time.days_ago", {
    values: { count: Math.floor(diff / 86400) },
    defaultMessage: "{{count}}d ago",
  });
};
